import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { AlertTriangle, Check, CloudLightning, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { useBreadcrumbLabel } from '@/components/breadcrumb';
import { cn } from '@/lib/cn';
import { badgeFromFlightStatus } from '@/lib/delay/format';
import { DelayBadge } from '@/components/delay/delay-badge';
import { useConnectionStore } from '@/state/connection.store';
import { joinDisruption, leaveDisruption } from '@/lib/ws/socket';
import {
  useDisruption,
  useDisruptionPriceBreakdown,
  useDisruptionProgress,
  useResolutionState,
} from '@/lib/disruptions/use-disruptions';
import type {
  DisruptionDetail,
  ResolutionState,
  RosterPriorityTag,
  RosterRebookingStatus,
} from '@/lib/disruptions/types';
import {
  STALENESS_TONE,
  STATUS_TONE,
  airportCodes,
  causeLabel,
  formatPriceMinor,
  formatDateTime,
  stalenessChip,
  statusLabel,
} from '../labels';
import { DisruptionViewSwitcher } from '../view-switcher';
import { RosterTab } from '../roster-tab';
import { EventAllocationTab } from '../event-allocation-tab';
import { CareDeskTab } from '../care-desk/care-desk-tab';
import { CostTab } from '../cost-tab';
import { TransportDeskTab } from '../transport-desk/transport-desk-tab';
import { TowerConsole } from './tower-console';
import { TowerInstruments } from './tower-instruments';
import { TowerRiver } from './tower-river';
import { useTowerJournal } from './tower-journal';
import { usePerFlightFacts } from './tower-per-flight';
import {
  buildInstruments,
  buildRiver,
  buildStrips,
  elapsedLabel,
  type TowerTarget,
  type TowerView,
  type TransferLens,
} from './tower-model';

// TOWER — the disruption control room (route `/disruptions/:urn`, the default view;
// `/tower` is an alias, the classic overview lives under `/classic`).
//
// A SECOND rendering of the disruption control center, side by side with the
// classic overview (`/disruptions/:urn`, untouched) so both can be compared on
// the same data. Same seams (detail, resolution-state@v1, progress over the WS
// room, resolve-next), a different shape:
//   · Command bar (56px) — friendly title + status + T+ · views · LIVE dot
//   · Stage — Board (instruments + flight strips + THE RIVER) or the
//     Passengers / Hotels / Cost views (the classic tabs' components, reused)
//   · Console (340px, pinned in every view) — NOW · IN PROGRESS · ATTENTION · LOG
// Body never scrolls; only leaf lists do (frontend-conventions §18).
//
// URL as state: the view lives in the hash (`#board` default; the classic hashes
// `#overview/#passengers/#allocation/#cost` are aliases so existing deep links
// survive) and a queue/pool/strip deep-link adds `?status=…&priority=…&flight=…`
// which the Passengers view applies as the roster's initial filters; the Hotels
// view takes `?hotel=…&transfer=PLAN|WAITING|EN_ROUTE|NO_SHOW|FAILED|RETURN` (a
// transfer pool / row / the NOW card's "Review plan" opens the shuttle board lens).

/** The tower stage's view set — tower-model's TowerView plus the Care desk
 *  (feat/care-desk K6) and the Transport desk (transport-desk T4). Extended
 *  LOCALLY: the frozen tower-model target grammar (queues / pools / instruments)
 *  never points at these desks, so `TowerTarget` stays on the narrower union. */
type StageView = TowerView | 'care' | 'transport';

const VIEWS: StageView[] = ['board', 'passengers', 'hotels', 'transport', 'care', 'cost'];
/** i18n keys — this module holds keys, never language. */
const VIEW_LABEL_KEY: Record<StageView, string> = {
  board: 'route.view.board',
  passengers: 'route.view.passengers',
  hotels: 'route.view.hotels',
  cost: 'route.view.cost',
  care: 'route.view.care',
  transport: 'route.view.transport',
};
const HASH_ALIAS: Record<string, StageView> = {
  overview: 'board',
  allocation: 'hotels',
};
const ROSTER_STATUSES: ReadonlySet<string> = new Set<RosterRebookingStatus>([
  'NOT_APPLICABLE',
  'PENDING',
  'OPTIONS_READY',
  'SELECTED',
  'NO_OPTIONS',
  'OVERRIDDEN',
  'DEFERRED',
]);

const TRANSFER_LENSES: ReadonlySet<string> = new Set<TransferLens>([
  'PLAN',
  'WAITING',
  'EN_ROUTE',
  'NO_SHOW',
  'FAILED',
  'RETURN',
]);

interface HashState {
  view: StageView;
  status: RosterRebookingStatus | undefined;
  priority: RosterPriorityTag | undefined;
  flightUrn: string | undefined;
  /** Hotels view: focus one hotel / a transfer lens. */
  hotel: string | undefined;
  transfer: TransferLens | undefined;
  /** Care view: pre-select one PNR on the desk (`#care?pnr=<subCaseUrn>` —
   *  the roster meal-voucher card's deep link, K6). */
  pnr: string | undefined;
}

function parseHash(hash: string): HashState {
  const stripped = hash.replace(/^#/, '');
  const [rawView = '', query = ''] = stripped.split('?');
  const view: StageView = (VIEWS as string[]).includes(rawView)
    ? (rawView as StageView)
    : (HASH_ALIAS[rawView] ?? 'board');
  const params = new URLSearchParams(query);
  const status = params.get('status');
  const priority = params.get('priority');
  const flightUrn = params.get('flight');
  const hotel = params.get('hotel');
  const transfer = params.get('transfer');
  const pnr = params.get('pnr');
  return {
    view,
    status: status && ROSTER_STATUSES.has(status) ? (status as RosterRebookingStatus) : undefined,
    priority: (priority as RosterPriorityTag | null) ?? undefined,
    flightUrn: flightUrn ?? undefined,
    hotel: hotel ?? undefined,
    transfer: transfer && TRANSFER_LENSES.has(transfer) ? (transfer as TransferLens) : undefined,
    pnr: pnr ?? undefined,
  };
}

function hashFor(view: StageView, target?: TowerTarget, flightUrn?: string | null): string {
  const params = new URLSearchParams();
  if (view === 'transport') {
    // The Transport desk's OWN hash grammar (`#transport?hotel=…&leg=out|return`)
    // — the tower's roster params mean nothing there, and the transfer lens maps
    // onto the desk's leg lens (RETURN → return, everything else → outbound).
    if (target?.hotel) params.set('hotel', target.hotel);
    if (target?.transfer) params.set('leg', target.transfer === 'RETURN' ? 'return' : 'out');
    const q = params.toString();
    return `#transport${q ? `?${q}` : ''}`;
  }
  if (target?.hint) params.set('status', target.hint);
  if (flightUrn) params.set('flight', flightUrn);
  if (target?.hotel) params.set('hotel', target.hotel);
  if (target?.transfer) params.set('transfer', target.transfer);
  const q = params.toString();
  return `#${view}${q ? `?${q}` : ''}`;
}

export function TowerRoute() {
  const { t } = useTranslation('tower');
  const { disruptionUrn: raw } = useParams<{ disruptionUrn: string }>();
  const urn = raw ? decodeURIComponent(raw) : undefined;
  const { data: detail, isLoading, isError, refetch } = useDisruption(urn);
  const { data: state, isLoading: stateLoading, isError: stateError } = useResolutionState(urn);
  const { hash } = useLocation();
  const navigate = useNavigate();
  const hs = useMemo(() => parseHash(hash), [hash]);
  const view = hs.view;
  const [selectedFlight, setSelectedFlight] = useState<string | null>(null);

  useBreadcrumbLabel(urn, detail?.title);

  useEffect(() => {
    if (!urn) return;
    joinDisruption(urn);
    return () => leaveDisruption(urn);
  }, [urn]);

  // WS-driven refresh of the aggregate (same mechanism as the classic cockpit): the
  // progress cache is fed by the disruption room (applyEvent → setQueryData); when
  // its snapshot MOVES — run status/counters, allocation run, group facts — re-read
  // resolution-state so the river, the instruments and the console follow live.
  // No interval poll here.
  const qc = useQueryClient();
  const { data: liveForFp } = useDisruptionProgress(urn);
  const g = liveForFp?.groups && 'groups' in liveForFp.groups ? liveForFp.groups : null;
  const fingerprint = liveForFp
    ? [
        liveForFp.runStatus,
        liveForFp.processedGroups ?? 0,
        liveForFp.roomsCommitted ?? 0,
        liveForFp.resolved,
        liveForFp.optionsGenerated,
        liveForFp.activeRun?.op ?? '',
        liveForFp.activeRun?.runId ?? '',
        liveForFp.activeRun?.progress?.done ?? '',
        liveForFp.allocationRun?.status ?? '',
        liveForFp.allocationRun?.processedGroups ?? '',
        liveForFp.allocationRun?.heldGroups ?? '',
        // MASS ground transport: the run broadcaster bumps these on every shuttle
        // run / boarding push — re-read the aggregate so the Transfers gate follows.
        liveForFp.transferRun
          ? `${liveForFp.transferRun.vehiclesPlanned}:${liveForFp.transferRun.vehiclesOnRoad}:${liveForFp.transferRun.arrivedRuns}:${liveForFp.transferRun.paxArrived}:${liveForFp.transferRun.paxTotal}:${liveForFp.transferRun.byHotel.map((h) => `${h.runs.planned}${h.runs.dispatched}${h.runs.boarding}${h.runs.inTransit}${h.runs.arrived}${h.runs.failed}`).join(',')}`
          : '',
        g
          ? `${g.assigned}:${g.ticketed}:${g.hotelHeld}:${g.hotelConfirmed}:${g.voucherIssued}:${g.awaiting}:${g.accepted}:${g.declined}:${g.resolved}:${g.offerReady ?? ''}:${g.offered ?? ''}:${g.issued ?? ''}:${g.commitFailed ?? ''}`
          : '',
      ].join('|')
    : null;
  useEffect(() => {
    if (!fingerprint || !urn) return;
    void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'resolution-state'] });
  }, [fingerprint, urn, qc]);

  const go = useCallback(
    (next: StageView, target?: TowerTarget, flightUrn?: string | null) => {
      navigate({ hash: hashFor(next, target, flightUrn) }, { replace: true });
    },
    [navigate],
  );

  // Keyboard: 1–5 switch views (never while typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const el = e.target as HTMLElement | null;
      if (
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable)
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = ['1', '2', '3', '4', '5'].indexOf(e.key);
      if (idx >= 0) go(VIEWS[idx]!);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  // A queue / pool / instrument opens its view — pre-filtered when it carries a
  // hint. transport-desk (T4): a TRANSFER target (the NOW card's "Review plan",
  // a shuttle pool, the transfer instruments — emitted as hotels+lens by the
  // console/model, which this slice doesn't own) now lands on the Transport desk,
  // where the runs board + run sheet live, instead of the Hotels view's pointer.
  const onTarget = useCallback(
    (t: TowerTarget) => {
      const next: StageView = t.transfer ? 'transport' : t.view;
      go(next, t, next === 'passengers' ? selectedFlight : null);
    },
    [go, selectedFlight],
  );
  // A flight strip (or its band in the river) SELECTS the flight: its slice lights
  // up across the river and the queues / Passengers open pre-filtered by it. The
  // strip's own "Passengers ›" link is the explicit way into the roster.
  const onSelectFlight = useCallback((_caseUrn: string | null, flightUrn: string | null) => {
    setSelectedFlight((prev) => (prev === flightUrn ? null : flightUrn));
  }, []);

  if (isError) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="tower-error">
        <div className="density-page min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <EmptyState
            icon={<AlertTriangle className="h-6 w-6" aria-hidden />}
            title={t('route.error.title')}
            description={t('route.error.description')}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => void refetch()}>{t('route.error.retry')}</Button>
                <Button variant="outline" asChild>
                  <Link to="/disruptions">{t('route.error.back')}</Link>
                </Button>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  if (isLoading || !detail || !urn) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="tower-loading">
        <div className="h-14 shrink-0 border-b border-border px-4 py-3">
          <Skeleton className="h-5 w-72" />
          <Skeleton className="mt-2 h-3 w-56" />
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-3 p-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
          <div className="space-y-3 border-l border-border p-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const rosterKey = `${hs.status ?? ''}|${hs.priority ?? ''}|${hs.flightUrn ?? ''}`;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="tower">
      <CommandBar detail={detail} state={state} view={view} onView={(v) => go(v)} />
      <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_auto] xl:grid-cols-[minmax(0,1fr)_340px] xl:grid-rows-1">
        <div className="flex min-h-0 flex-col xl:border-r xl:border-border">
          {view === 'board' ? (
            <Board
              urn={urn}
              detail={detail}
              state={state}
              stateLoading={stateLoading}
              stateError={stateError}
              onTarget={onTarget}
              selectedFlight={selectedFlight}
              onSelectFlight={onSelectFlight}
            />
          ) : view === 'passengers' ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* key: a new deep-link (queue / pool / strip) remounts the roster with its
                  initial filters; the chips stay the source of truth afterwards. */}
              <RosterTab
                key={rosterKey}
                disruptionUrn={urn}
                compact
                initialFilters={{
                  rebookingStatus: hs.status,
                  priority: hs.priority,
                  flightUrn: hs.flightUrn,
                }}
              />
            </div>
          ) : view === 'hotels' ? (
            <div className="density-page flex min-h-0 flex-1 flex-col overflow-hidden p-4 sm:p-6">
              <EventAllocationTab
                disruptionUrn={urn}
                detail={detail}
                focusHotel={hs.hotel}
                transferLens={hs.transfer}
              />
            </div>
          ) : view === 'transport' ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* transport-desk (T4): the desk reads its own deep-link grammar off
                  the SAME `#transport?…` hash the tower routes on — hotel/leg/run/pnr
                  params work here exactly as on the classic tab. Mounted only in
                  this branch (lazy enable, like the other reused classic tabs). */}
              <TransportDeskTab disruptionUrn={urn} enabled />
            </div>
          ) : view === 'cost' ? (
            <div className="density-page min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <CostTab disruptionUrn={urn} enabled />
            </div>
          ) : (
            // Care desk (K6) — the classic tab's component, reused like the others.
            // Only mounted while active (the tower renders one view at a time), so
            // `enabled` mirrors the CostTab lazy pattern; `?pnr=` pre-selects a row.
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <CareDeskTab disruptionUrn={urn} enabled focusPnr={hs.pnr} />
            </div>
          )}
        </div>
        {state ? (
          <TowerConsole
            disruptionUrn={urn}
            detail={detail}
            state={state}
            onTarget={onTarget}
            className="max-h-[46vh] min-h-0 overflow-y-auto border-t border-border xl:max-h-none xl:overflow-visible xl:border-t-0"
          />
        ) : (
          <aside
            className="space-y-3 border-t border-border p-4 xl:border-t-0"
            aria-label={t('console.aria')}
          >
            {stateError ? (
              <EmptyState
                title={t('route.state.errorTitle')}
                description={t('route.state.errorDescription')}
              />
            ) : (
              <>
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-40 w-full" />
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

function Board({
  urn,
  detail,
  state,
  stateLoading,
  stateError,
  onTarget,
  selectedFlight,
  onSelectFlight,
}: {
  urn: string;
  detail: DisruptionDetail;
  state: ResolutionState | undefined;
  stateLoading: boolean;
  stateError: boolean;
  onTarget: (t: TowerTarget) => void;
  selectedFlight: string | null;
  onSelectFlight: (caseUrn: string | null, flightUrn: string | null) => void;
}) {
  const { t } = useTranslation('tower');
  const navigate = useNavigate();
  const { data: live } = useDisruptionProgress(urn);
  const { data: costs } = useDisruptionPriceBreakdown(urn, true);
  const { data: perFlight } = usePerFlightFacts(urn, true);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  // airline-money@v2 — per-flight PRICES from the price breakdown (null = not available).
  const priceByCase = useMemo(
    () => new Map((costs?.flights ?? []).map((f) => [f.caseUrn, f.priceMinor] as const)),
    [costs],
  );
  const strips = useMemo(
    () => buildStrips(detail, now, priceByCase, perFlight),
    [detail, now, priceByCase, perFlight],
  );
  const instruments = useMemo(
    () =>
      state
        ? buildInstruments(state, detail, now, (minor, currency) => formatPriceMinor(minor, currency, t), live, t)
        : null,
    [state, detail, now, live, t],
  );
  const pendingOp = useTowerJournal((s) => s.pending[urn])?.op ?? null;
  const river = useMemo(
    () => (state ? buildRiver(state, live, detail, pendingOp, strips, t) : null),
    [state, live, detail, pendingOp, strips, t],
  );
  const selectedCase = useMemo(
    () => strips.find((s) => s.flightUrn && s.flightUrn === selectedFlight)?.caseUrn ?? null,
    [strips, selectedFlight],
  );

  if (stateError) {
    return (
      <div className="p-4">
        <EmptyState
          title={t('route.board.errorTitle')}
          description={t('route.board.errorDescription')}
        />
      </div>
    );
  }
  if (stateLoading || !state || !instruments || !river) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4" data-testid="tower-board-loading">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="min-h-0 w-full flex-1" />
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="tower-board">
      <TowerInstruments items={instruments} onTarget={onTarget} />
      {/* The flights live INSIDE the river's entrance lane (one card per band) —
          no separate strips column: the river already starts with the flights. */}
      <div className="grid min-h-0 flex-1 grid-cols-1">
        <div className="relative min-h-0 bg-muted/20 p-2">
          <div className="absolute left-4 top-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {t('route.river.label')}{' '}
            {river.known
              ? t('route.river.groups', { n: river.total.toLocaleString() })
              : t('route.river.notLoaded')}
          </div>
          <div
            className="absolute right-4 top-2 flex items-center gap-3 text-[11px] text-muted-foreground"
            aria-hidden
          >
            <span className="inline-flex items-center gap-1">
              <i
                className="inline-block h-2 w-3.5 rounded-sm"
                style={{ background: 'var(--status-ok, #1F8A5B)' }}
              />
              {t('route.legend.done')}
            </span>
            <span className="inline-flex items-center gap-1">
              <i
                className="inline-block h-2 w-3.5 rounded-sm"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(135deg, hsl(var(--primary)) 0 2px, transparent 2px 4px)',
                }}
              />
              {t('route.legend.reversible')}
            </span>
            <span className="inline-flex items-center gap-1">
              <i
                className="inline-block h-2 w-3.5 rounded-sm"
                style={{ background: 'var(--status-pending, #E8C98A)' }}
              />
              {t('route.legend.pending')}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="inline-block h-2 w-3.5 rounded-sm bg-border" />
              {t('route.legend.notApplicable')}
            </span>
          </div>
          <div className="h-full pt-5">
            <TowerRiver
              model={river}
              currency={state.kpis.currency}
              onTarget={onTarget}
              selectedCase={selectedCase}
              onSelectCase={(caseUrn) => {
                const strip = strips.find((s) => s.caseUrn === caseUrn);
                onSelectFlight(caseUrn, strip?.flightUrn ?? null);
              }}
              onOpenPassengers={(caseUrn) => {
                const strip = strips.find((s) => s.caseUrn === caseUrn);
                if (strip?.flightUrn) {
                  onSelectFlight(caseUrn, strip.flightUrn);
                  onTarget({ view: 'passengers', hint: undefined });
                }
              }}
              onOpenCase={(caseUrn) => navigate(`/cases/${encodeURIComponent(caseUrn)}`)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CommandBar({
  detail,
  state,
  view,
  onView,
}: {
  detail: DisruptionDetail;
  state: ResolutionState | undefined;
  view: StageView;
  onView: (v: StageView) => void;
}) {
  const { t } = useTranslation('tower');
  const connected = useConnectionStore((s) => s.connected);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const airports = airportCodes(detail.scope.airports);
  const pax = state?.kpis.affectedPassengers ?? detail.rollup.affectedPassengers;
  const flights = state?.kpis.caseCount ?? detail.rollup.caseCount;
  const facts = [
    airports || null,
    causeLabel(detail.cause, t),
    t('count.flights', { count: flights }),
    t('route.pax', { n: pax.toLocaleString() }),
  ]
    .filter(Boolean)
    .join(' · ');
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(detail.urn);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no-op */
    }
  };
  return (
    <header
      className="grid h-14 shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 border-b border-border bg-background px-4"
      data-testid="tower-command-bar"
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <CloudLightning className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <h1 className="truncate text-[15px] font-semibold leading-tight">{detail.title}</h1>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-px text-[11px] font-semibold',
              STATUS_TONE[detail.status],
            )}
            data-testid="tower-status"
          >
            {statusLabel(detail.status, t)}
          </span>
          {detail.staleness && detail.staleness.state !== 'OK' ? (
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-px text-[11px] font-semibold',
                STALENESS_TONE[detail.staleness.severity],
              )}
              title={detail.staleness.reason}
            >
              ⚠ {stalenessChip(detail.staleness, t)}
            </span>
          ) : null}
          {/* Flight status (flight-delay U1, `flightStatus@v1`) — one line, never wraps
              the 56px bar: the title truncates first. */}
          <DelayBadge
            model={badgeFromFlightStatus(detail.flightStatus)}
            showTimes={false}
            className="shrink-0 flex-nowrap"
          />
        </div>
        <p className="truncate text-[11.5px] text-muted-foreground">
          {facts} · {t('route.detected', { at: formatDateTime(detail.detectedAt) })} ·{' '}
          <b className="font-medium text-foreground/80">
            T+{elapsedLabel(detail.detectedAt, now, t)}
          </b>
          {detail.containedAt
            ? t('route.containedAt', { at: formatDateTime(detail.containedAt) })
            : t('route.windowOpen')}
        </p>
      </div>
      <div
        className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5"
        role="tablist"
        aria-label={t('route.viewsAria')}
      >
        {VIEWS.map((v, i) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => onView(v)}
            className={cn(
              'rounded-md px-3 py-1 text-[12.5px] transition-colors',
              view === v
                ? 'bg-background font-semibold text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid={`tower-view-${v}`}
          >
            {t(VIEW_LABEL_KEY[v])}{' '}
            <span className="ml-1 text-[10px] text-muted-foreground">{i + 1}</span>
          </button>
        ))}
      </div>
      <div className="flex min-w-0 items-center justify-end gap-3 text-xs text-muted-foreground">
        <span
          className="inline-flex items-center gap-1.5"
          title={connected ? t('route.ws.connected') : t('route.ws.reconnecting')}
        >
          <i
            className={cn(
              'inline-block h-2 w-2 rounded-full',
              connected
                ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,.2)]'
                : 'animate-pulse bg-amber-500',
            )}
            aria-hidden
          />
          {connected ? t('route.ws.live') : t('route.ws.reconnectingShort')}
        </span>
        <button
          type="button"
          onClick={() => void copy()}
          className="inline-flex items-center gap-1 hover:text-foreground"
          title={t('route.copyUrn', { urn: detail.urn })}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
          <span className="sr-only">{t('route.copyUrnSr')}</span>
        </button>
        <DisruptionViewSwitcher urn={detail.urn} current="tower" />
      </div>
    </header>
  );
}
